import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import NodePropertiesPanel from './NodePropertiesPanel';
import { NODE_TYPES_CONFIG } from './WorkflowNode';
import {
  PanelRightClose, PanelRightOpen, MessageSquare, Link2, SlidersHorizontal,
  Globe, Server, Bot, X, Plus, Zap, BarChart3, Clock, Trash2, Sparkles,
  LayoutGrid, TrendingDown, TrendingUp, Landmark, Repeat, Search,
} from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/UI/accordion';
import Badge from '@/components/UI/Badge';
import { useApollo } from '@/context/ApolloContext';
import { messagesAPI, brokersAPI, sessionsAPI } from '@/services/apolloApi';
import { strategiesAPI } from '@/services/api';
import { useSession } from '@/lib/authClient';

const STRATEGY_ICONS = {
  grid: LayoutGrid,
  mean_reversion: TrendingDown,
  momentum: TrendingUp,
  market_making: Landmark,
  arbitrage: Repeat,
};

const STRATEGY_LABELS = {
  grid: 'Grid Trading',
  mean_reversion: 'Mean Reversion',
  momentum: 'Momentum',
  market_making: 'Market Making',
  arbitrage: 'Arbitrage',
};

const isSystemBroker = (b) => b?.config?.system === true;

function ChatHistorySection() {
  const { activeBrokerId, activeSessionId } = useApollo();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [brokers, setBrokers] = useState([]);
  const [brokerSessions, setBrokerSessions] = useState({}); // { brokerId: sessions[] }
  const [showArchived, setShowArchived] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { sessionId, x, y }

  useEffect(() => {
    if (!session?.user?.id) return;
    brokersAPI.getAll().then((all) => {
      all.sort((a, b) => {
        if (isSystemBroker(a)) return -1;
        if (isSystemBroker(b)) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setBrokers(all);
      // Fetch sessions for each broker
      all.forEach((b) => {
        sessionsAPI.getAll(b.id).then(({ sessions }) => {
          setBrokerSessions((prev) => ({ ...prev, [b.id]: sessions }));
        }).catch(() => {});
      });
    }).catch(() => {});
  }, [session?.user?.id, activeSessionId]); // re-fetch when session changes (new chat created)

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [contextMenu]);

  async function handleNewChat(brokerId) {
    try {
      const newSession = await sessionsAPI.create(brokerId);
      navigate(`/apollo/chat/${brokerId}/${newSession.id}`);
    } catch (err) { console.error('Failed to create session:', err); }
  }

  async function handleArchive(sid) {
    setContextMenu(null);
    try {
      await sessionsAPI.archive(sid);
      setBrokerSessions((prev) => {
        const updated = {};
        for (const [bid, sessions] of Object.entries(prev)) {
          updated[bid] = sessions.map((s) => s.id === sid ? { ...s, status: 'archived' } : s);
        }
        return updated;
      });
    } catch (err) { console.error('Archive failed:', err); }
  }

  async function handleDelete(sid) {
    setContextMenu(null);
    if (!confirm('Delete this chat and all its messages?')) return;
    try {
      await sessionsAPI.delete(sid);
      setBrokerSessions((prev) => {
        const updated = {};
        for (const [bid, sessions] of Object.entries(prev)) {
          updated[bid] = sessions.filter((s) => s.id !== sid);
        }
        return updated;
      });
      if (sid === activeSessionId) {
        navigate(`/apollo/chat/${activeBrokerId}`);
      }
    } catch (err) { console.error('Delete failed:', err); }
  }

  return (
    <div className="space-y-3">
      {brokers.length === 0 ? (
        <p className="text-xs text-muted-foreground/60 py-2">No brokers yet</p>
      ) : (
        brokers.map((b) => {
          const isSys = isSystemBroker(b);
          const sessions = brokerSessions[b.id] || [];
          const activeSessions = sessions.filter((s) => s.status === 'active');
          const archivedSessions = sessions.filter((s) => s.status === 'archived');
          const isActiveBroker = b.id === activeBrokerId;

          return (
            <div key={b.id}>
              {/* Broker header */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  {isSys ? <Sparkles className="w-3 h-3 text-primary" /> : <Bot className="w-3 h-3 text-primary" />}
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{b.name}</span>
                </div>
                <button
                  onClick={() => handleNewChat(b.id)}
                  className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
                  title="New chat"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>

              {/* Active sessions */}
              {activeSessions.length === 0 && (
                <p className="text-[10px] text-muted-foreground/40 pl-4.5 mb-1">No chats yet</p>
              )}
              {activeSessions.map((s) => {
                const isActiveSession = s.id === activeSessionId;
                return (
                  <div
                    key={s.id}
                    className={`group flex items-center rounded-md transition-colors mb-0.5 ${
                      isActiveSession ? 'bg-primary/15 border border-primary/20' : 'hover:bg-accent/10'
                    }`}
                  >
                    <button
                      onClick={() => navigate(`/apollo/chat/${b.id}/${s.id}`)}
                      onContextMenu={(e) => { e.preventDefault(); setContextMenu({ sessionId: s.id, x: e.clientX, y: e.clientY }); }}
                      className="flex-1 text-left px-2.5 py-1.5 min-w-0"
                    >
                      <p className="text-xs text-foreground truncate">{s.title}</p>
                      {s.last_message && (
                        <p className="text-[10px] text-muted-foreground/50 truncate mt-0.5">{s.last_message.slice(0, 50)}</p>
                      )}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}
                      className="p-1 mr-1 rounded text-muted-foreground/0 group-hover:text-muted-foreground/50 hover:!text-red-400 transition-colors flex-shrink-0"
                      title="Delete chat"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}

              {/* Archived toggle */}
              {archivedSessions.length > 0 && (
                <>
                  <button
                    onClick={() => setShowArchived(!showArchived)}
                    className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors pl-2 mt-1"
                  >
                    {showArchived ? 'Hide' : 'Show'} archived ({archivedSessions.length})
                  </button>
                  {showArchived && archivedSessions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => navigate(`/apollo/chat/${b.id}/${s.id}`)}
                      onContextMenu={(e) => { e.preventDefault(); setContextMenu({ sessionId: s.id, x: e.clientX, y: e.clientY }); }}
                      className="w-full text-left px-2.5 py-1 rounded-md hover:bg-accent/5 transition-colors mb-0.5 opacity-50"
                    >
                      <p className="text-xs text-muted-foreground truncate">{s.title}</p>
                    </button>
                  ))}
                </>
              )}
            </div>
          );
        })
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed bg-card border border-white/10 rounded-lg shadow-xl py-1 min-w-[140px] z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleArchive(contextMenu.sessionId)}
            className="w-full text-left px-3 py-1.5 text-xs text-foreground hover:bg-accent/10 transition-colors"
          >
            Archive
          </button>
          <button
            onClick={() => handleDelete(contextMenu.sessionId)}
            className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-red-400/10 transition-colors"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ── Strategy Picker Modal ──
function StrategyPickerModal({ open, onClose, onSelect, excludeIds = [] }) {
  const [strategies, setStrategies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    strategiesAPI.getAll()
      .then((res) => setStrategies(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = strategies.filter((s) => {
    if (excludeIds.includes(s.id)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.type.toLowerCase().includes(q);
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-card border border-white/10 rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[70vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Add Strategy</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Select a strategy to link to this chat</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-white/5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search strategies..."
              className="w-full bg-accent/10 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>
        </div>

        {/* Strategy list */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-muted-foreground">
                {strategies.length === 0 ? 'No strategies found. Create one in the Strategy Builder.' : 'No matching strategies.'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((s) => {
                const Icon = STRATEGY_ICONS[s.type] || Zap;
                const params = typeof s.parameters === 'string' ? JSON.parse(s.parameters) : (s.parameters || {});
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      onSelect(s);
                      onClose();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/10 transition-colors text-left group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{s.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-muted-foreground">{STRATEGY_LABELS[s.type] || s.type}</span>
                        {params.symbol && (
                          <Badge variant="info" className="text-[9px] py-0 px-1.5">{params.symbol}</Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground/50 capitalize">{s.status || 'idle'}</span>
                      </div>
                    </div>
                    <Plus className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary transition-colors flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContextSection() {
  const { linkedContext, removeLinkedItem, addLinkedItem } = useApollo();
  const [showStrategyModal, setShowStrategyModal] = useState(false);

  const linkedStrategies = linkedContext.strategies || [];
  const linkedSymbols = linkedContext.symbols || [];

  return (
    <div className="space-y-3">
      {/* Linked strategies */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-muted-foreground/60 uppercase">Strategies</span>
          <button
            onClick={() => setShowStrategyModal(true)}
            className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
            title="Add strategy"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
        {linkedStrategies.length === 0 && (
          <p className="text-[10px] text-muted-foreground/40">No strategies linked</p>
        )}
        {linkedStrategies.map((s) => {
          const Icon = STRATEGY_ICONS[s.type] || Zap;
          return (
            <div key={s.id} className="flex items-center justify-between px-2 py-1 rounded-md bg-accent/5 mb-1">
              <div className="flex items-center gap-1.5">
                <Icon className="w-3 h-3 text-primary" />
                <span className="text-xs text-foreground truncate">{s.name}</span>
              </div>
              <button onClick={() => removeLinkedItem('strategies', s.id)} className="text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}

        {/* Strategy Picker Modal */}
        <StrategyPickerModal
          open={showStrategyModal}
          onClose={() => setShowStrategyModal(false)}
          onSelect={(s) => addLinkedItem('strategies', { id: s.id, name: s.name, type: s.type })}
          excludeIds={linkedStrategies.map((s) => s.id)}
        />
      </div>

      {/* Linked symbols */}
      <div>
        <span className="text-[10px] text-muted-foreground/60 uppercase">Symbols</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {linkedSymbols.map((s) => (
            <Badge key={s.id} variant="info" className="text-[10px] gap-1">
              {s.name}
              <button onClick={() => removeLinkedItem('symbols', s.id)}>
                <X className="w-2.5 h-2.5" />
              </button>
            </Badge>
          ))}
          {linkedSymbols.length === 0 && (
            <p className="text-[10px] text-muted-foreground/40">No symbols linked</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ParametersSection() {
  const { chatParams, setChatParams } = useApollo();

  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] text-muted-foreground/60 uppercase block mb-1">Model</label>
        <div className="flex gap-1.5">
          {['haiku', 'sonnet'].map((m) => (
            <button
              key={m}
              onClick={() => setChatParams((p) => ({ ...p, model: m }))}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                chatParams.model === m
                  ? 'bg-primary/15 text-primary border border-primary/20'
                  : 'text-muted-foreground border border-white/5 hover:bg-accent/10'
              }`}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground/60 uppercase block mb-1">
          Temperature: {chatParams.temperature}
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={chatParams.temperature}
          onChange={(e) => setChatParams((p) => ({ ...p, temperature: parseFloat(e.target.value) }))}
          className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-accent/20 accent-primary"
        />
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground/60 uppercase block mb-1">Max Tokens</label>
        <select
          value={chatParams.maxTokens}
          onChange={(e) => setChatParams((p) => ({ ...p, maxTokens: parseInt(e.target.value) }))}
          className="w-full bg-accent/10 border border-white/5 rounded-md px-2 py-1.5 text-xs text-foreground"
        >
          <option value={1024}>1,024</option>
          <option value={2048}>2,048</option>
          <option value={4096}>4,096</option>
          <option value={8192}>8,192</option>
        </select>
      </div>
    </div>
  );
}

function ExternalSourcesSection() {
  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground/40">
        Connect external data sources to enrich broker context.
      </p>
      <div className="space-y-1">
        {[
          { name: 'News Feed', icon: Globe, status: 'coming soon' },
          { name: 'TradingView', icon: BarChart3, status: 'coming soon' },
          { name: 'Custom API', icon: Server, status: 'coming soon' },
        ].map((source) => (
          <div key={source.name} className="flex items-center justify-between px-2 py-1.5 rounded-md bg-accent/5">
            <div className="flex items-center gap-2">
              <source.icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-foreground">{source.name}</span>
            </div>
            <span className="text-[9px] text-muted-foreground/40">{source.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProviderSection() {
  const { data: session } = useSession();
  const { linkedContext, addLinkedItem, removeLinkedItem } = useApollo();
  const linkedProviders = linkedContext.brokers || [];

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground/40">
        Broker connections available for strategy management.
      </p>
      {linkedProviders.length === 0 ? (
        <p className="text-[10px] text-muted-foreground/40">No providers linked — broker will use all available connections.</p>
      ) : (
        linkedProviders.map((p) => (
          <div key={p.id} className="flex items-center justify-between px-2 py-1.5 rounded-md bg-accent/5">
            <span className="text-xs text-foreground">{p.name}</span>
            <button onClick={() => removeLinkedItem('brokers', p.id)} className="text-muted-foreground hover:text-foreground">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))
      )}
    </div>
  );
}

const NODE_PALETTE = [
  { category: 'Input', types: ['market_data', 'strategy_status', 'timer'] },
  { category: 'Analysis', types: ['ai_analysis', 'indicator_check'] },
  { category: 'Condition', types: ['threshold', 'if_else'] },
  { category: 'Action', types: ['adjust_strategy', 'start_stop', 'notify'] },
];

function NodePalette() {
  const onDragStart = (e, nodeType) => {
    e.dataTransfer.setData('application/reactflow-nodetype', nodeType);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-muted-foreground/50">Drag a node onto the canvas to add it</p>
      {NODE_PALETTE.map((group) => (
        <div key={group.category}>
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mb-1.5">{group.category}</p>
          <div className="space-y-1">
            {group.types.map((type) => {
              const cfg = NODE_TYPES_CONFIG[type];
              if (!cfg) return null;
              return (
                <div
                  key={type}
                  draggable
                  onDragStart={(e) => onDragStart(e, type)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-grab active:cursor-grabbing transition-colors hover:bg-accent/10 ${cfg.color}`}
                >
                  <span className="text-sm shrink-0">{cfg.icon}</span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground">{cfg.label}</p>
                    {cfg.description && <p className="text-[10px] text-muted-foreground/60 leading-tight">{cfg.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function WorkflowDrawerContent() {
  const { selectedNode, setSelectedNode } = useApollo();

  const handleConfigChange = (newConfig) => {
    if (!selectedNode) return;
    setSelectedNode({
      ...selectedNode,
      data: { ...selectedNode.data, config: newConfig },
    });
  };

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3">
      {selectedNode ? (
        <>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Node Properties
          </p>
          <NodePropertiesPanel node={selectedNode} onConfigChange={handleConfigChange} />
        </>
      ) : (
        <>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Node Palette
          </p>
          <NodePalette />
        </>
      )}
    </div>
  );
}

function DrawerContent() {
  const { workflowMode, selectedNode, setSelectedNode } = useApollo();

  if (workflowMode) {
    return <WorkflowDrawerContent />;
  }

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3">
      <Accordion type="multiple" defaultValue={['chat-history', 'context']}>
        <AccordionItem value="chat-history">
          <AccordionTrigger>
            <div className="flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />
              Chat History
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ChatHistorySection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="context">
          <AccordionTrigger>
            <div className="flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" />
              Context
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ContextSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="parameters">
          <AccordionTrigger>
            <div className="flex items-center gap-1.5">
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Parameters
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ParametersSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="external-sources">
          <AccordionTrigger>
            <div className="flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" />
              External Sources
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ExternalSourcesSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="provider">
          <AccordionTrigger>
            <div className="flex items-center gap-1.5">
              <Server className="w-3.5 h-3.5" />
              Provider
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ProviderSection />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function ApolloDrawer() {
  const { drawerOpen, setDrawerOpen } = useApollo();
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <>
      {/* Toggle button — always visible */}
      <button
        onClick={() => setDrawerOpen(!drawerOpen)}
        className="absolute right-0 top-3 z-20 p-1.5 rounded-l-md bg-card/80 border border-r-0 border-white/10 text-muted-foreground hover:text-foreground transition-colors"
        title={drawerOpen ? 'Collapse drawer' : 'Expand drawer'}
      >
        {drawerOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
      </button>

      <AnimatePresence>
        {drawerOpen && isMobile && (
          <>
            {/* Mobile: overlay backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-30"
              onClick={() => setDrawerOpen(false)}
            />
            {/* Mobile: slide-over panel */}
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 h-full w-[280px] z-40 border-l border-white/10 bg-card backdrop-blur-xl flex flex-col overflow-hidden"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 shrink-0">
                <span className="text-xs font-semibold text-foreground">Apollo</span>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <DrawerContent />
            </motion.aside>
          </>
        )}

        {/* Desktop: inline push panel */}
        {drawerOpen && !isMobile && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="h-full border-l border-white/10 bg-card/40 backdrop-blur-xl flex flex-col overflow-hidden flex-shrink-0"
          >
            <DrawerContent />
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

export default ApolloDrawer;
